import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

interface ILocationDetail {
  id: number
  code: string
  name: string
}

export interface IMappingBetweenLocationGroupAndLocation {
  [locationGroupId: number]: {
    locationGroupId: number
    locationGroupName: string
    locationGroupType: string
    locations: ILocationDetail[]
  }
}

const gqlQuery = gql`
  query ListLocationsInEachLocationGroup(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
  ) {
    listLocationGroups(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        locationGroupName
        locationGroupType
        locationId
        code
        name
      }
    }
  }
`

function useListLocationsInEachLocationGroup(
  startDate: string,
  endDate: string,
  locationGroupIds: number[],
) {
  const { data, loading } = useQuery(gqlQuery, {
    variables: {
      iStartDate: startDate,
      iEndDate: endDate,
      iFilter: {
        location_group_ids: locationGroupIds,
      },
    },
    skip:
      !startDate ||
      !endDate ||
      !locationGroupIds ||
      locationGroupIds.length === 0,
  })

  return {
    isLoading: loading,
    data: useMemo(() => {
      if (!data?.listLocationGroups?.nodes) {
        return null
      }

      const mapping: IMappingBetweenLocationGroupAndLocation = {}

      // Group locations by locationGroupId
      data.listLocationGroups.nodes.forEach((node: any) => {
        const {
          locationGroupId,
          locationGroupName,
          locationGroupType,
          locationId,
          code,
          name,
        } = node

        // Initialize the location group if it doesn't exist
        if (!mapping[locationGroupId]) {
          mapping[locationGroupId] = {
            locationGroupId,
            locationGroupName,
            locationGroupType,
            locations: [],
          }
        }

        // Add the location to the group (avoid duplicates)
        const existingLocation = mapping[locationGroupId].locations.find(
          (loc) => loc.id === locationId,
        )

        if (!existingLocation && locationId) {
          mapping[locationGroupId].locations.push({
            id: locationId,
            code: code || '',
            name: name || '',
          })
        }
      })

      // Sort locations within each group by code for consistency
      Object.values(mapping).forEach((group) => {
        group.locations.sort((a: ILocationDetail, b: ILocationDetail) =>
          a.code.localeCompare(b.code),
        )
      })

      return mapping
    }, [data]),
  }
}

export default useListLocationsInEachLocationGroup
